import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import { Seo } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { CITIES, cityPath, REGIONS } from "../data/cities";

export default function CitiesIndex() {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => CITIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())),
    [q]
  );

  return (
    <div data-testid="cities-index-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-8">
      <Seo
        title="Ontario Security Service Areas | Security Guards Near You"
        description="1st Nations Security serves 45+ Ontario cities: Thunder Bay, Toronto, Ottawa, Hamilton, London, Kitchener, Sudbury, Kenora & more. Security guards near you. +1 807-770-0243."
        path="/ontario-service-areas"
      />
      <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">1st Nations Security — Ontario Security Services</p>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white font-heading mb-5">
        Security Guard Services by Ontario City
      </h1>
      <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-8">
        Searching for a security guard company near you? 1st Nations Security provides licensed security guards,
        mobile patrol, construction site security, event security, fire watch and alarm response in every region of
        Ontario — {REGIONS.join(", ")} and beyond. Select your city for local security services.
      </p>

      <div className="relative max-w-md mb-10">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find your Ontario city…"
          data-testid="city-search-input"
          className="w-full rounded-full border border-white/10 bg-slate-900/70 pl-11 pr-4 py-3 text-sm text-white placeholder-slate-500 outline-none focus:border-amber-500/50"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Link
            key={c.slug}
            to={cityPath(c)}
            data-testid={`cities-index-card-${c.slug}`}
            className="group rounded-2xl border border-white/10 bg-slate-900/60 p-5 transition-all duration-200 hover:border-amber-500/40 hover:bg-slate-900"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <MapPin className="h-4 w-4 text-amber-500" />
              <h2 className="text-lg font-semibold text-white group-hover:text-amber-300 transition-colors">{c.name}</h2>
            </div>
            <p className="text-xs text-slate-500 mb-2">{c.region}</p>
            <p className="text-sm text-slate-400">Security guard services in {c.name}, Ontario</p>
          </Link>
        ))}
      </div>
      {filtered.length === 0 && (
        <p data-testid="city-search-empty" className="text-sm text-slate-400 mt-6">
          No matching city in our directory — but we serve all of Ontario. Call +1 807-770-0243 to confirm coverage in your community.
        </p>
      )}

      <CtaBand
        heading="Your City Not Listed?"
        sub="We serve every region of Ontario — including rural, remote and First Nations communities. Call our 24/7 dispatch to confirm coverage for your location."
      />
    </div>
  );
}
